import { Grid } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
    Button,
    ExtendedTheme,
    Heading,
    Loader,
    LoadMore,
    Option,
    Select,
    TextBlock
} from 'neon-design-system';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BackButton } from '../../../../components/BackButton/BackButton';
import { ClickableListItem } from '../../../../components/ClickableListItem/ClickableListItem';
import { ItemsList } from '../../../../components/ItemsList/ItemsList';
import { NoRequestsTile } from '../../../../components/NoRequestsTile/NoRequestsTile';
import { baseUrls } from '../../../../constants/urls';
import { AccountsQueryParams } from '../../../../hooks/useAccountsQuery';
import { usePaginationRequestInfo } from '../../../../providers/PaginationRequestProvider';
import { CashMovementActivity } from '../../../../types/request';
import { localDateFormat } from '../../../../utils/date';
import { formatToLocaleString } from '../../../../utils/number';
import { CashMovementStatusLabel } from '../../components/CashMovementStatusLabel/CashMovementStatusLabel';
import { activeFilterName } from './CashMovementRequests';
import {
    getCashMovementActivityDetailPath,
    getCashMovementActivityTypeLabel
} from '../../utils/activity';

interface Props {
    cashMovements: CashMovementActivity[];
    options: Option[];
    selectedAccounts: Option[] | null;
    setSelectedAccounts: (option: Option[] | null) => void;
    debounceGetAccounts: (params?: AccountsQueryParams) => Promise<any> | void;
    isFetching: boolean;
    receiptsAvailable: boolean;
}

const useStyles = makeStyles((theme: ExtendedTheme) =>
    createStyles({
        relative: {
            '& > div ': {
                position: 'relative'
            }
        },
        requestWithdrawalButton: {
            width: '100%'
        },
        heading: {
            margin: theme.spacing(0.5, 0)
        },
        paymentTypeAndDateSpacing: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5)
        }
    })
);

const SeparatorDot = () => (
    <svg width="4" height="4" viewBox="0 0 4 4" fill="none">
        <circle cx="2" cy="2" r="2" fill="#737373" />
    </svg>
);

export const MobileCashMovementRequests = (props: Props) => {
    const {
        cashMovements,
        selectedAccounts,
        setSelectedAccounts,
        options,
        debounceGetAccounts,
        isFetching,
        receiptsAvailable
    } = props;
    const [mobileCashMovements, setMobileCashMovements] = useState<CashMovementActivity[]>([]);
    const { paginationInfo, setPaginationInfo } = usePaginationRequestInfo(activeFilterName);
    const history = useHistory();
    const classes = useStyles();
    const showMore = () => {
        setPaginationInfo({
            pageNumber: paginationInfo?.pageNumber ? paginationInfo?.pageNumber + 1 : 1,
            pageSize: 15
        });
    };

    useEffect(() => {
        const allItems = [...mobileCashMovements, ...cashMovements];
        const uniqueIds = new Set(allItems.map((item) => item.activityId));
        const value = Array.from(
            Array.from(uniqueIds.values()).map(
                (id) => allItems.find((item) => item.activityId === id)!
            )
        );
        value.sort(
            (a, b) => new Date(b.activityDate).getTime() - new Date(a.activityDate).getTime()
        );
        setMobileCashMovements(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cashMovements]);

    const ShowMoreRequests = () => {
        if (!mobileCashMovements.length && !cashMovements.length) {
            return <NoRequestsTile />;
        }

        return !mobileCashMovements.length ? null : (
            <LoadMore
                totalCount={paginationInfo?.totalRecords || 0}
                shownCount={mobileCashMovements.length}
                buttonText="Show more requests"
                showMore={showMore}
            />
        );
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <BackButton to={'/'} pageName="Support Center" />
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Heading styleName="largeTitle" xs="small">
                                    Cash movements
                                </Heading>
                            </Grid>
                            <Grid item xs={12}>
                                <TextBlock styleName="largeBody2" xs="small" shade={400}>
                                    Below is a list of your requests to move cash.
                                </TextBlock>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            size="large"
                            className={classes.requestWithdrawalButton}
                            colorType="primary"
                            onClick={() =>
                                history.push(
                                    baseUrls.cashMovementNewRequest.replace(
                                        ':paymentType',
                                        'withdrawal'
                                    )
                                )
                            }
                        >
                            Request withdrawal
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Select
                            value={selectedAccounts}
                            loadOptions={(searchString) => debounceGetAccounts({ searchString })}
                            defaultOptions={options}
                            placeholder="Select accounts…"
                            onChange={(value) => {
                                setSelectedAccounts(value);
                                setMobileCashMovements([]);
                            }}
                            multi
                        />
                    </Grid>
                </Grid>
            </Grid>
            {!receiptsAvailable && (
                <Grid item xs={12} role="status">
                    <TextBlock shade={400}>
                        Receipt allocations are temporarily unavailable. Payment activity may still be
                        shown.
                    </TextBlock>
                </Grid>
            )}
            {!!mobileCashMovements.length && (
                <Grid item xs={12}>
                    <ItemsList>
                        {mobileCashMovements.map((g: CashMovementActivity) => (
                            <ClickableListItem
                                key={g.activityId}
                                titleComponent={
                                    <TextBlock styleName="footnote">
                                        {g.activityType === 'PaymentRequest'
                                            ? g.paymentRequest.accountName
                                            : g.accountNumber}
                                    </TextBlock>
                                }
                                statusComponent={<CashMovementStatusLabel status={g.status} />}
                                onClick={
                                    getCashMovementActivityDetailPath(g)
                                        ? () => history.push(getCashMovementActivityDetailPath(g)!)
                                        : undefined
                                }
                            >
                                <Grid container>
                                    <Grid item xs={12}>
                                        <TextBlock className={classes.heading}>
                                            <strong>
                                                {g.currency}{' '}
                                                {formatToLocaleString(g.amount, {
                                                    minimumFractionDigits: 2
                                                })}
                                            </strong>
                                        </TextBlock>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div className={classes.paymentTypeAndDateSpacing}>
                                            {getCashMovementActivityTypeLabel(g)}
                                            <SeparatorDot />
                                            {localDateFormat(g.activityDate)}
                                        </div>
                                    </Grid>
                                </Grid>
                            </ClickableListItem>
                        ))}
                    </ItemsList>
                </Grid>
            )}
            <Grid item xs={12}>
                {isFetching ? (
                    <div className={classes.relative}>
                        <Loader />
                    </div>
                ) : (
                    <ShowMoreRequests />
                )}
            </Grid>
        </Grid>
    );
};
